/* eslint-disable react/no-multi-comp */
/* eslint-disable react/display-name */
import React, { Fragment, Suspense } from "react";

import { Redirect, Route, RouteComponentProps, Switch } from "react-router-dom";
import { RouteConfig, RouteConfigComponentProps } from "react-router-config";

import {
  AccessCode,
  Accounts,
  AddLearner,
  AddNewArea,
  AreasCovered,
  BrandSelector,
  Calendar,
  Error401,
  Error404,
  Error408,
  Error500,
  Home,
  ManageArea,
  ManageLearners,
  Messages,
  MyBusinessProfile,
  MyDetails,
  Preferences,
  RandomSignIn,
  SetPassword,
  SignIn,
  SignUp,
  ViewLearner,
} from "@hubcapp/views";
import { Auth, BrandSelect, Dashboard, Error } from "@hubcapp/layouts";
import { Brand } from "@hubcapp/types";
import { LoadingScreen, Page, PageAccessManagement } from "@hubcapp/components";

import { PageType } from "./components/PageAccessManagement/types";
import { ViewLearnerProps } from "./views/Learners/ViewLearner/types";
import AcceptancePageCheckStateProvider from "./views/AcceptancePageCheck";
import AcceptancePageResultStateProvider from "./views/AcceptancePageResult";
import AcceptancePageStateProvider from "./views/AcceptancePageComponent/StateProvider";
import ProfileDetails from "./views/MyBusinessProfile/ProfileDetails/StateProvider";

const routesConfig: RouteConfig[] = [
  {
    exact: true,
    path: "/",
    component: () => <Redirect to="/home" />,
  },
  {
    path: "/brand-select",
    layout: BrandSelect,
    exact: true,
    component: () => (
      <Page title="Select your franchise brand">
        <BrandSelector />
      </Page>
    ),
  },
  {
    path: "/:brand(bsm|aa)",
    layout: Auth,
    routes: [
      {
        path: "/:brand(bsm|aa)/sign-up",
        exact: true,
        component: () => (
          <Page title="Sign up">
            <SignUp />
          </Page>
        ),
      },
      {
        path: "/:brand(bsm|aa)/change-password",
        exact: true,
        component: () => (
          <Page title="Change password">
            <SignUp />
          </Page>
        ),
      },
      {
        path: "/:brand(bsm|aa)/sign-in",
        exact: true,
        component: () => (
          <Page title="Sign in">
            <SignIn />
          </Page>
        ),
      },
      {
        path: "/:brand(bsm|aa)/random-sign-in",
        exact: true,
        component: (props: RouteComponentProps<{ brand: string }>) => (
          <>
            {process.env.REACT_APP_HUBCAPP_ENABLE_SIMPLE_AUTH === "true" ? (
              <Page title="Sign in">
                <RandomSignIn
                  brand={
                    props?.match?.params?.brand?.toLowerCase() === "aa"
                      ? Brand.Aads
                      : Brand.Bsm
                  }
                />
              </Page>
            ) : (
              <Redirect to="/home" />
            )}
          </>
        ),
      },
      {
        path: "/:brand(bsm|aa)/access-code",
        exact: true,
        component: () => (
          <Page title="Access code">
            <AccessCode />
          </Page>
        ),
      },
      {
        path: "/:brand(bsm|aa)/set-password",
        exact: true,
        component: () => (
          <Page title="Access code">
            <SetPassword />
          </Page>
        ),
      },
      {
        component: () => <Redirect to="/errors/error-404" />,
      },
    ],
  },
  {
    path: "/acceptance-page-check/:brand/:personKey/:agreementKey",
    exact: true,
    routes: [
      {
        component: (
          props: RouteConfigComponentProps<{
            brand: string;
            personKey: string;
            agreementKey: string;
          }>,
        ) => (
          <AcceptancePageCheckStateProvider
            brand={props.match.params?.brand}
            personKey={props.match.params?.personKey}
            agreementKey={props.match.params?.agreementKey}
          />
        ),
      },
    ],
  },
  {
    path: "/learners",
    layout: Dashboard,
    routes: [
      {
        path: "/learners/add",
        exact: true,
        component: () => (
          <Page title="Add a learner">
            <PageAccessManagement pageType={PageType.AddLearner}>
              <AddLearner />
            </PageAccessManagement>
          </Page>
        ),
      },
      {
        path: "/learners/:learnerId",
        exact: true,
        component: (props: RouteConfigComponentProps<ViewLearnerProps>) => (
          <Page title="View a learner">
            <PageAccessManagement pageType={PageType.ViewLearner}>
              <ViewLearner {...props.match.params} />
            </PageAccessManagement>
          </Page>
        ),
      },
      {
        component: () => (
          <Page title="Manage learners">
            <PageAccessManagement pageType={PageType.ViewLearners}>
              <ManageLearners />
            </PageAccessManagement>
          </Page>
        ),
      },
      {
        component: () => <Redirect to="/errors/error-404" />,
      },
    ],
  },
  {
    path: "/messages/:selectedTab(news|tech-updates)?/:id?",
    exact: true,
    layout: Dashboard,
    routes: [
      {
        component: (props: RouteComponentProps<{ selectedTab: string }>) => {
          const selectedTab = props?.match?.params?.selectedTab;
          let pageType: PageType;

          switch (selectedTab?.toLowerCase()) {
            case "news":
              pageType = PageType.News;
              break;
            case "tech-updates":
              pageType = PageType.TechUpdates;
              break;
            default:
              pageType = PageType.Messages;
          }

          return (
            <Page title="Messages">
              <PageAccessManagement pageType={pageType}>
                <Messages />
              </PageAccessManagement>
            </Page>
          );
        },
      },
      {
        component: () => <Redirect to="/errors/error-404" />,
      },
    ],
  },
  {
    path: "/acceptance-page-result/:result/:brand/:agreementKey/", // Brand y personId
    exact: true,
    routes: [
      {
        component: (
          props: RouteConfigComponentProps<{
            brand: string;
            agreementKey: string;
            result: string;
          }>,
        ) => (
          <AcceptancePageResultStateProvider
            brand={props.match.params?.brand}
            result={props.match.params?.result}
            agreementKey={props.match.params?.agreementKey}
          />
        ),
      },
    ],
  },
  {
    path: "/acceptance-page/:agreementKey", // Brand y personId
    exact: true,
    routes: [
      {
        component: (
          props: RouteConfigComponentProps<{
            agreementKey: string;
          }>,
        ) => (
          <AcceptancePageStateProvider
            agreementKey={props.match.params?.agreementKey}
          />
        ),
      },
    ],
  },
  {
    path: "/calendar",
    layout: Dashboard,
    routes: [
      {
        component: () => (
          <Page title="Calendar">
            <PageAccessManagement pageType={PageType.Calendar}>
              <Calendar />
            </PageAccessManagement>
          </Page>
        ),
      },
      {
        component: () => <Redirect to="/errors/error-404" />,
      },
    ],
  },
  {
    path: "/accounts/:selectedTab(transactions|invoices)?/:id?",
    layout: Dashboard,
    routes: [
      {
        component: (props: RouteComponentProps<{ selectedTab: string }>) => {
          const selectedTab = props?.match?.params?.selectedTab;
          let pageType: PageType;

          switch (selectedTab?.toLowerCase()) {
            case "transactions":
              pageType = PageType.AccountTransactions;
              break;
            case "invoices":
              pageType = PageType.AccountInvoices;
              break;
            default:
              pageType = PageType.AccountTransactions;
          }

          return (
            <Page title="Accounts">
              <PageAccessManagement pageType={pageType}>
                <Accounts />
              </PageAccessManagement>
            </Page>
          );
        },
      },
      {
        component: () => <Redirect to="/errors/error-404" />,
      },
    ],
  },
  {
    path: "/my-business-profile",
    layout: Dashboard,
    routes: [
      {
        path: "/my-business-profile",
        exact: true,
        component: () => (
          <Page title="My business profile">
            <PageAccessManagement pageType={PageType.MyBusinessProfile}>
              <MyBusinessProfile />
            </PageAccessManagement>
          </Page>
        ),
      },
      {
        path: "/my-business-profile/profile-details",
        exact: true,
        component: () => (
          <Page title="Profile details">
            <PageAccessManagement pageType={PageType.MyBusinessProfile}>
              <ProfileDetails />
            </PageAccessManagement>
          </Page>
        ),
      },
      {
        component: () => <Redirect to="/errors/error-404" />,
      },
    ],
  },
  {
    path: "/settings",
    layout: Dashboard,
    routes: [
      {
        path: "/settings/areas-and-prices",
        routes: [
          {
            path: "/settings/areas-and-prices",
            exact: true,
            component: () => (
              <Page title="Manage coverage">
                <PageAccessManagement pageType={PageType.AreasCovered}>
                  <AreasCovered />
                </PageAccessManagement>
              </Page>
            ),
          },
          {
            path: "/settings/areas-and-prices/area/:id",
            exact: true,
            component: (props: RouteComponentProps<{ id: string }>) => (
              <Page title="Coverage area">
                <PageAccessManagement pageType={PageType.ManageArea}>
                  <ManageArea id={props?.match?.params?.id} />
                </PageAccessManagement>
              </Page>
            ),
          },
          {
            path: "/settings/areas-and-prices/add",
            exact: true,
            component: () => (
              <Page title="Add an area">
                <PageAccessManagement pageType={PageType.AddANewArea}>
                  <AddNewArea />
                </PageAccessManagement>
              </Page>
            ),
          },
          {
            component: () => <Redirect to="/errors/error-404" />,
          },
        ],
      },
      {
        path: "/settings/preferences",
        exact: true,
        component: () => (
          <Page title="Preferences">
            <PageAccessManagement pageType={PageType.Preferences}>
              <Preferences />
            </PageAccessManagement>
          </Page>
        ),
      },
      {
        path: "/settings/my-details",
        exact: true,
        component: () => (
          <Page title="My details">
            <PageAccessManagement pageType={PageType.MyDetails}>
              <MyDetails />
            </PageAccessManagement>
          </Page>
        ),
      },
    ],
  },
  {
    path: "/errors",
    layout: Error,
    routes: [
      {
        path: "/errors/error-401",
        exact: true,
        component: () => <Error401 />,
      },
      {
        path: "/errors/error-404",
        exact: true,
        layout: Dashboard,
        component: () => <Error404 />,
      },
      {
        path: "/errors/error-408",
        exact: true,
        component: () => <Error408 />,
      },
      {
        path: "/errors/error-500",
        exact: true,
        component: () => <Error500 />,
      },
      {
        component: () => <Redirect to="/errors/error-404" />,
      },
    ],
  },
  {
    path: "*",
    layout: Dashboard,
    routes: [
      {
        path: "/home",
        exact: true,
        component: () => (
          <Page title="Home">
            <PageAccessManagement pageType={PageType.Home}>
              <Home />
            </PageAccessManagement>
          </Page>
        ),
      },
      {
        component: () => <Redirect to="/errors/error-404" />,
      },
    ],
  },
];

const renderRoutes = (routes: RouteConfig[]) =>
  routes ? (
    <Suspense fallback={<LoadingScreen />}>
      <Switch>
        {routes.map((route, i) => {
          const Layout = route.layout || Fragment;
          const Component = route.component;

          return (
            <Route
              key={i}
              path={route.path}
              exact={route.exact}
              render={(props: any) => (
                <Layout>
                  {route.routes ? (
                    renderRoutes(route.routes)
                  ) : (
                    // @ts-ignore
                    <Component {...props} />
                  )}
                </Layout>
              )}
            />
          );
        })}
      </Switch>
    </Suspense>
  ) : null;

export default () => renderRoutes(routesConfig);
